<template>
  <div>
    <div class="vue-lists">
      <slot name="header">
        <div class="title-date">
          <h4 :class="{'mb-1': titleDate}">{{ $t('agenda.appointments.title')}}</h4>
          <span v-if="titleDate" class="mb-3">{{ titleDate }}</span>
        </div>
      </slot>

      <!-- Import Campaign Section -->
      <div v-if="hasPermissionToAddCampaign">
        <div class="d-flex justify-content-start">
          <input ref="importCampaign" id="campaign-input" type="file" @input="onCampaignSelected" hidden accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, .csv">
          <a href="#" @click.prevent="uploadFile"><span class="fa fa-upload mr-1"></span>{{$t('agenda.campaigns.import_facebook')}}</a>
          <loading-animation v-if="fetchingServer" class="ml-2" :size="'14px'"></loading-animation>
        </div>
        <expand-transition v-if="campaignErrors.has('file')">
          <div key="1" class="d-flex h-100">
            <small class="help text-danger">
              {{$t(campaignErrors.get('file'))}}
            </small>
          </div>
        </expand-transition>
        <progress-bar class="mb-2" v-if="fetchingServer" :progress="uploadProgress"></progress-bar>
      </div>

      <div v-if="appointments.length" class="d-flex appointment-item-details-wrapper">
        <ul ref="appointmentWrapper" id="appointment-ul-wrapper" class="vue-unordered w-100">
          <div :ref="`appointment${appointment.id}`" :id="`appointment${appointment.id}`" v-for="appointment in appointments" :key="appointment.id" :class="{'appointment-li-active': appointment.id === id, 'appointment-li': appointment.id !== id}">
            <li class="d-flex justify-content-start align-items-center appointment-list-item" style="padding-bottom: 2px;">
              <span class="d-flex justify-content-start align-items-center appointment-time">
                {{ appointment.start | timeFormat }} - {{ appointment.end | timeFormat }}&nbsp;
                <span style="font-size: 14px;">({{$t(`appointment_types.${appointment.type.id}`, appointment.type.name)}})</span>&nbsp;
                <span v-if="appointment.campaign_id" style="font-size: 14px;"><i class="fa fa-bullhorn"></i></span>
              </span>
            </li>
            <div class="card-subtitle mb ml-4" :class="{'text-white': appointment.id === id, 'text-dark': appointment.id !== id}">
              {{$t('agenda.appointments.patientRegister')}}:
              <span v-if="appointment.person" class="ml-1">{{ appointment.person.name }}
                <i v-if="appointment.person.access" :class="{'fa fa-wheelchair': appointment.person.access === 1}" aria-hidden="true"></i>
              </span>
            </div>
            <div class="card-subtitle mb-2 ml-4" :class="{'text-white': appointment.id === id, 'text-dark': appointment.id !== id}">
              {{$t('agenda.appointments.phone')}}:
              <span class="ml-1" v-if="appointment.person.phone">{{ appointment.person.phone }} </span>
              <span class="ml-1" v-else>---</span>
            </div>
          </div>
        </ul>
      </div>
      <div v-else class="d-flex justify-content-center mt-3">
        <span class="font-weight-bold">{{$t('agenda.appointments.no_appointments')}}</span>
      </div>
    </div>
  </div>
</template>
<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import LoadingAnimation from '@/components/common/loaders/LoadingCircle'
import ExpandTransition from '@/components/common/transitions/ExpandTransition'
import ProgressBar from '@/components/common/UI/ProgressBar'
import Errors from '@/utils/StandardErrors.js'

const campaignInfoType = 600

export default {
  components: {
    LoadingAnimation,
    ExpandTransition,
    ProgressBar
  },
  props: {
    screening: {
      type: Object,
      required: true
    },
    id: {
      type: Number,
      required: false,
      default: -1
    },
    appointments: {
      type: Array,
      required: false,
      default: () => []
    },
    showTitle: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  data () {
    return {
      file: [],
      fileTypes: null,
      uploadProgress: 0,
      allowedExtensions: ['xlsx', 'xls', 'csv'],
      fetchingServer: false,
      campaignErrors: new Errors()
    }
  },
  methods: {
    onCampaignSelected: function (event) {
      let files = event.target.files || event.dataTransfer.files

      if (!files.length) {
        return
      }

      this.file = files[0]

      if (!this.allowedExtensions.includes(this.file.name.split('.').pop())) {
        this.campaignErrors.record({ file: ['File does not have valid extension.'] }, 'agenda.campaigns.errors.')
        return
      }

      this.importFacebookCampaign()
    },
    uploadFile: function () {
      this.campaignErrors.clear()
      this.$refs.importCampaign.click()
    },
    importFacebookCampaign: function (url = `${this.$store.state.ebooking_server}/screenings/${this.screening.id}/campaigns`) {
      this.fetchingServer = true
      let formData = new FormData()
      formData.append('file', this.file)
      formData.append('campaign_type_id', 1)
      this.$http.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: function (progressEvent) {
          this.uploadProgress = parseInt(Math.round((progressEvent.loaded * 100) / progressEvent.total))
        }.bind(this)
      }).then((response) => {
        this.file = []
        let appointments = response.data.data.appointments
        this.showToast(this.$t('agenda.campaigns.imported_appointments', { number: appointments.length }), this.$store.state.successToastOptions)
        this.$emit('campaign-added', { appointments })
      }).catch((err) => {
        if (err.response.status === 422) {
          this.campaignErrors.record(err.response.data.errors, 'agenda.campaigns.errors.')
        }
        console.log(err)
      }).finally(() => {
        this.fetchingServer = false
        this.uploadProgress = 0
        this.$refs.importCampaign.value = ''
      })
    }
  },
  computed: {
    ...mapGetters([
      'services',
    ]),
    currentService: function () {
      return this.services.find(s => s.url === this.$store.state.ebooking_server)
    },
    hasPermissionToAddCampaign: function () {
      return this.currentService.user_permissions.some(permission => permission.info_type === campaignInfoType && permission.access_type >= 2 && (permission.entity_id === this.screening.facility.entity_id || permission.entity_id === null))
    },
    titleDate: function () {
      if (this.appointments.length) {
        return this.$options.filters.localTime(this.appointments[0].start, 'dddd, LL', '', this.selectedLanguage)
        return this.moment(this.appointments[0].start, 'YYYY-MM-DD').locale(this.selectedLanguage).format('dddd, LL')
      }
      return null
    },
    selectedLanguage: function () {
      return this.$i18n.locale()
    },
  },
  filters: {
    timeFormat: (value) => {
      return moment(value, 'YYYY-MM-DD HH:mm:ss').format('HH:mm')
    }
  }
}
</script>
<style lang="scss">
.title-appointments{
  display:flex;
  justify-content: center;
}

.title-date{
  font-size: 16px;
  color:grey;
}

.appointment-time{
  font-size: 1.25rem;
  font-weight: bold;
  margin-left: 0.5rem;
}

.card{
  border: none;
}

.appointment-li-active {
  background-color: #29c4a9;
  margin-top: 5px;
  padding-top: 2px;
  padding-bottom: 2px;
  color: white;
  width: 100%;
  border-radius: 8px;
}

.appointment-li {
  background-color: #f0f0f0;
  margin-top: 5px;
  padding-top: 2px;
  padding-bottom: 2px;
  width: 100%;
  border-radius: 8px;
}

ul > li.appointment-list-item{
  display: none !important;
}

#appointment-ul-wrapper.vue-unordered li::before {
  display: none !important;
}

.appointment-item-details-wrapper {
  max-height: 60vh;
  overflow-y: auto;
}

</style>
